/* ==========================================================================
   Cherry Pot — shop sheet (added 2026-10-10)

   One fast bottom sheet that replaces three slow paths:

   1. SIZE SHEET — opened by
        - the sticky Add to cart bar on the product page (sized products),
        - "Choose size" on every product card (theme cards + homepage rails),
        - "Choose size" in the cart drawer's "You may also like" rail.
      Shows the sizes as big buttons. One tap on a size adds it to the cart,
      closes the sheet and opens the cart drawer.

   2. QUICK VIEW — opened by the round eye button on every product card.
      Photos, price, sizes, quantity, Add to cart, Buy it now, and a link to
      the full product page.

   WHY: the old Quick View (and the card buttons that reused it) downloaded
   the whole product page — about 725 KB of HTML with every app on it — and
   parsed it before showing anything. That was the ~5 second wait. The sheet
   now opens from data already printed on the card (snippets/cp-shop-data.liquid),
   so it shows immediately. Quick View fetches the product's extra photos from
   /products/<handle>.js (about 8 KB) after it opens.

   Adding to the cart uses /cart/add.js and then fires the theme's own
   `cart:update` event with the same shape Horizon's product form uses, so the
   cart bubble, the drawer auto-open and the drawer contents all react exactly
   as they do for a normal add (same contract as assets/cp-card-add.js).

   If anything fails, every trigger falls back to the product page, so a
   shopper can never get stuck.

   Classes are prefixed `cpss-` (sheet) and `cpqv-` (eye button).
   Styles: assets/cp-shop-sheet.css.
   ========================================================================== */
(() => {
  if (window.__cpShopSheet) return;
  window.__cpShopSheet = true;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const EYE =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></svg>';
  const CLOSE =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  const CHECK =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';

  /* ------------------------------------------------------------------ */
  /* Money                                                              */
  /* ------------------------------------------------------------------ */

  const moneyFormat = window.cpMoneyFormat || 'Rs.{{amount}}';

  /** Formats cents with the shop's money format (only used for /products/*.js data). */
  const formatMoney = (cents) => {
    const value = Number(cents) / 100;
    const group = (n, d, t, dec) => {
      const fixed = n.toFixed(d);
      const [int, frac] = fixed.split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (frac ? dec + frac : '');
    };
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return group(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(value, 0, '.', ',');
        default:
          return group(value, 2, ',', '.');
      }
    });
  };

  /** "Rs.1,500.00" -> "Rs.1,500" for the compact size buttons. */
  const shortMoney = (text) => String(text || '').replace(/[.,]00(?=\D*$)/, '');

  /* ------------------------------------------------------------------ */
  /* Product data                                                       */
  /* ------------------------------------------------------------------ */

  /** @type {Map<string, any>} handle -> normalised product */
  const products = new Map();
  /** @type {Map<string, Promise<any>>} handle -> pending /products/x.js */
  const fullRequests = new Map();

  /** Normalises the compact card data (snippets/cp-shop-data.liquid). */
  const fromCompact = (raw) => {
    const d = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return {
      handle: d.h,
      title: d.t,
      url: d.u || ROOT + 'products/' + d.h,
      image: d.i || '',
      images: d.i ? [d.i] : [],
      options: (d.o || []).map((name) => ({ name })),
      variants: (d.v || []).map((v) => ({
        id: v[0],
        options: v[1] || [],
        price: v[2],
        compare: v[3] || '',
        available: !!v[4],
        image: '',
      })),
      full: false,
    };
  };

  /** Normalises /products/<handle>.js. */
  const fromFull = (j) => ({
    handle: j.handle,
    title: j.title,
    url: j.url || ROOT + 'products/' + j.handle,
    image: j.featured_image ? sized(j.featured_image, 600) : '',
    images: (j.images || []).map((src) => sized(src, 900)),
    options: (j.options || []).map((o) => ({ name: typeof o === 'string' ? o : o.name })),
    variants: (j.variants || []).map((v) => ({
      id: v.id,
      options: v.options || [],
      price: formatMoney(v.price),
      compare: v.compare_at_price && v.compare_at_price > v.price ? formatMoney(v.compare_at_price) : '',
      available: !!v.available,
      image: v.featured_image && v.featured_image.src ? sized(v.featured_image.src, 900) : '',
    })),
    full: true,
  });

  /** Adds a width to a Shopify CDN image URL. */
  const sized = (src, width) => {
    if (!src) return '';
    try {
      const url = new URL(src.startsWith('//') ? 'https:' + src : src, location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (e) {
      return src;
    }
  };

  const handleFromUrl = (href) => {
    const m = String(href || '').match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  };

  /** Loads the full product (photos, variant images). Cached per page view. */
  const loadFull = (handle) => {
    if (!handle) return Promise.reject(new Error('no handle'));
    const known = products.get(handle);
    if (known && known.full) return Promise.resolve(known);
    if (fullRequests.has(handle)) return fullRequests.get(handle);

    const request = fetch(ROOT + 'products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' },
    })
      .then((r) => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then((j) => {
        const p = fromFull(j);
        products.set(handle, p);
        return p;
      })
      .catch((e) => {
        fullRequests.delete(handle);
        throw e;
      });

    fullRequests.set(handle, request);
    return request;
  };

  /** Best data available right now for an element, without waiting. */
  const quickData = (el) => {
    const holder = el.closest('[data-cpss]') || el.querySelector('[data-cpss]');
    if (holder) {
      try {
        const p = fromCompact(holder.getAttribute('data-cpss'));
        const known = products.get(p.handle);
        if (known && known.full) return known;
        products.set(p.handle, p);
        return p;
      } catch (e) {
        /* fall through */
      }
    }
    return null;
  };

  const pdpData = () => {
    const script = document.getElementById('cpss-pdp');
    if (!script) return null;
    try {
      const p = fromCompact(script.textContent);
      const known = products.get(p.handle);
      if (known && known.full) return known;
      products.set(p.handle, p);
      return p;
    } catch (e) {
      return null;
    }
  };

  const isSized = (p) => !!p && p.variants.length > 1;

  /* ------------------------------------------------------------------ */
  /* Cart                                                               */
  /* ------------------------------------------------------------------ */

  /** Section ids the cart UI needs re-rendered after an add (same as cp-card-add.js). */
  const cartSectionIds = () => {
    const ids = [];
    document
      .querySelectorAll('cart-drawer-component, cart-items-component, cart-icon, [id^="shopify-section-"] .cart-drawer')
      .forEach((node) => {
        const section = node.closest('.shopify-section, [id^="shopify-section-"]');
        if (!section || !section.id) return;
        const id = section.id.replace('shopify-section-', '');
        if (id && !ids.includes(id)) ids.push(id);
      });
    return ids;
  };

  /** Adds a variant. Resolves with the cart JSON, rejects with a message. */
  const addToCart = async (variantId, quantity) => {
    const body = new FormData();
    body.set('id', String(variantId));
    body.set('quantity', String(quantity || 1));
    const sections = cartSectionIds();
    if (sections.length) body.set('sections', sections.join(','));

    const res = await fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.description || json.message || 'Could not add this item. Please try again.');
    return json;
  };

  /** Tells the theme an item was added — bubble, drawer and contents update. */
  const announceAdd = (json) => {
    const added = json.items ? json.items : [json];
    let count = 0;
    added.forEach((i) => (count += i.quantity || 0));
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: json,
          sourceId: 'cp-shop-sheet',
          data: {
            source: 'product-form-component',
            itemCount: count || 1,
            variantId: added[0] && added[0].variant_id,
            productId: added[0] && added[0].product_id,
            sections: json.sections,
          },
        },
      })
    );
  };

  const track = (name) => {
    try {
      if (window.clarity) window.clarity('event', name);
    } catch (e) {
      /* no-op */
    }
  };

  /* ------------------------------------------------------------------ */
  /* The sheet shell (one <dialog>, reused)                             */
  /* ------------------------------------------------------------------ */

  let dialog = null;
  let closing = 0;

  const escapeHtml = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const getDialog = () => {
    if (dialog && dialog.isConnected) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'cpss';
    dialog.setAttribute('aria-labelledby', 'cpss-title');
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      closeSheet();
    });
    // Tap on the dim backdrop closes.
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) closeSheet();
    });
    document.body.appendChild(dialog);
    return dialog;
  };

  const openShell = (html, mode) => {
    const d = getDialog();
    clearTimeout(closing);
    d.className = 'cpss cpss--' + mode;
    d.innerHTML =
      '<div class="cpss__panel">' +
      '<span class="cpss__grab" aria-hidden="true"></span>' +
      '<button type="button" class="cpss__close" aria-label="Close">' + CLOSE + '</button>' +
      html +
      '</div>';
    d.querySelector('.cpss__close').addEventListener('click', closeSheet);
    if (!d.open) {
      try {
        d.showModal();
      } catch (e) {
        d.setAttribute('open', '');
      }
    }
    document.documentElement.classList.add('cpss-lock');
    requestAnimationFrame(() => requestAnimationFrame(() => d.classList.add('is-open')));
    return d;
  };

  const closeSheet = () => {
    const d = dialog;
    if (!d || !d.open) return;
    d.classList.remove('is-open');
    document.documentElement.classList.remove('cpss-lock');
    clearTimeout(closing);
    closing = setTimeout(() => {
      try {
        d.close();
      } catch (e) {
        d.removeAttribute('open');
      }
      d.innerHTML = '';
    }, 220);
  };

  const headHtml = (p, priceText, compareText) =>
    '<div class="cpss__head">' +
    (p.image ? '<img class="cpss__thumb" src="' + escapeHtml(p.image) + '" alt="" width="64" height="64">' : '') +
    '<div class="cpss__head-text">' +
    '<a class="cpss__title" id="cpss-title" href="' + escapeHtml(p.url) + '">' + escapeHtml(p.title) + '</a>' +
    '<p class="cpss__price"><span class="cpss__now">' + escapeHtml(priceText) + '</span>' +
    (compareText ? ' <s class="cpss__was">' + escapeHtml(compareText) + '</s>' : '') +
    '</p></div></div>';

  /** Price line for the sheet head: one price, or "From" the cheapest. */
  const priceSummary = (p) => {
    const prices = [...new Set(p.variants.map((v) => v.price))];
    const cheapest = p.variants.reduce((a, b) => (priceNumber(b.price) < priceNumber(a.price) ? b : a), p.variants[0]);
    if (prices.length <= 1) return { now: cheapest.price, was: cheapest.compare };
    return { now: 'From ' + shortMoney(cheapest.price), was: '' };
  };

  const priceNumber = (text) => Number(String(text || '').replace(/[^\d.]/g, '')) || 0;

  const optionLabel = (name) => {
    const n = String(name || '').trim();
    if (!n || n.toLowerCase() === 'title') return 'Option';
    return n.charAt(0).toUpperCase() + n.slice(1);
  };

  /** Variants matching the chosen values for every option before `upTo`. */
  const matching = (p, chosen, upTo) =>
    p.variants.filter((v) => {
      for (let i = 0; i < upTo; i++) if (chosen[i] != null && v.options[i] !== chosen[i]) return false;
      return true;
    });

  const valuesFor = (p, index) => {
    const seen = [];
    p.variants.forEach((v) => {
      const value = v.options[index];
      if (value != null && !seen.includes(value)) seen.push(value);
    });
    return seen;
  };

  /* ------------------------------------------------------------------ */
  /* 1. Size sheet                                                      */
  /* ------------------------------------------------------------------ */

  /**
   * @param {any} p product
   * @param {{ currentId?: string|number }} [opts]
   */
  const openSizeSheet = (p, opts = {}) => {
    const lastIndex = p.options.length - 1;
    const current = p.variants.find((v) => String(v.id) === String(opts.currentId || ''));
    // Every option except the last is a "pick" row; the last row adds on tap.
    const chosen = p.options.map((_, i) => {
      if (i === lastIndex) return null;
      const from = current || p.variants.find((v) => v.available) || p.variants[0];
      return from ? from.options[i] : null;
    });

    const pricesDiffer = new Set(p.variants.map((v) => v.price)).size > 1;
    const head = priceSummary(p);

    const renderRows = () =>
      p.options
        .map((opt, i) => {
          const pool = i === 0 ? p.variants : matching(p, chosen, i);
          const values = valuesFor(p, i);
          const isAddRow = i === lastIndex;
          const chips = values
            .map((value) => {
              const hits = pool.filter((v) => v.options[i] === value);
              const variant = isAddRow ? hits[0] : null;
              const available = hits.some((v) => v.available);
              const selected = !isAddRow && chosen[i] === value;
              const isCurrent = isAddRow && current && current.options[i] === value && matching(p, chosen, i).includes(current);
              const priceLine = isAddRow && variant && pricesDiffer ? '<span class="cpss__chip-price">' + escapeHtml(shortMoney(variant.price)) + '</span>' : '';
              return (
                '<button type="button" class="cpss__chip' +
                (isAddRow ? ' cpss__chip--add' : '') +
                (selected ? ' is-selected' : '') +
                (isCurrent ? ' is-current' : '') +
                '" data-row="' + i + '" data-value="' + escapeHtml(value) + '"' +
                (variant ? ' data-variant="' + variant.id + '"' : '') +
                (isAddRow ? '' : ' aria-pressed="' + (selected ? 'true' : 'false') + '"') +
                (available ? '' : ' disabled') +
                '><span class="cpss__chip-value">' + escapeHtml(value) + '</span>' +
                (available ? priceLine : '<span class="cpss__chip-price">Sold out</span>') +
                '<span class="cpss__chip-state" aria-hidden="true">' + CHECK + '</span>' +
                '</button>'
              );
            })
            .join('');
          return (
            '<div class="cpss__row" role="group" aria-label="' + escapeHtml(optionLabel(opt.name)) + '">' +
            '<p class="cpss__label">' + (isAddRow ? 'Select ' : '') + escapeHtml(optionLabel(opt.name).toLowerCase() === 'option' ? 'option' : optionLabel(opt.name).toLowerCase()) + '</p>' +
            '<div class="cpss__chips">' + chips + '</div></div>'
          );
        })
        .join('');

    const d = openShell(
      headHtml(p, head.now, head.was) +
        '<div class="cpss__body">' + renderRows() + '</div>' +
        '<p class="cpss__hint">Tap a ' + escapeHtml(optionLabel(p.options[lastIndex] && p.options[lastIndex].name).toLowerCase()) + ' to add it to your cart</p>' +
        '<p class="cpss__error" role="alert" hidden></p>',
      'size'
    );

    const body = d.querySelector('.cpss__body');
    const error = d.querySelector('.cpss__error');
    let busy = false;

    body.addEventListener('click', async (event) => {
      const chip = event.target instanceof Element ? event.target.closest('.cpss__chip') : null;
      if (!chip || chip.disabled || busy) return;
      const row = Number(chip.dataset.row);

      if (row !== lastIndex) {
        chosen[row] = chip.dataset.value;
        for (let i = row + 1; i < lastIndex; i++) {
          const pool = matching(p, chosen, i);
          if (!pool.some((v) => v.options[i] === chosen[i])) chosen[i] = pool[0] ? pool[0].options[i] : null;
        }
        body.innerHTML = renderRows();
        return;
      }

      busy = true;
      error.hidden = true;
      chip.classList.add('is-busy');
      d.classList.add('is-busy');
      try {
        const json = await addToCart(chip.dataset.variant, 1);
        chip.classList.remove('is-busy');
        chip.classList.add('is-done');
        track('sizesheet_add');
        setTimeout(() => {
          closeSheet();
          announceAdd(json);
        }, 380);
      } catch (e) {
        chip.classList.remove('is-busy');
        d.classList.remove('is-busy');
        error.textContent = e.message;
        error.hidden = false;
        busy = false;
      }
    });

    track('sizesheet_open');
    // Focus the close button, not a size: a focus ring on a size reads as
    // "already selected". Keyboard users tab straight into the sizes.
    const closeBtn = d.querySelector('.cpss__close');
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  };

  /* ------------------------------------------------------------------ */
  /* 2. Quick View                                                      */
  /* ------------------------------------------------------------------ */

  const openQuickView = (p) => {
    const chosen = (() => {
      const first = p.variants.find((v) => v.available) || p.variants[0];
      return p.options.map((_, i) => (first ? first.options[i] : null));
    })();
    let qty = 1;

    const selectedVariant = () => p.variants.find((v) => v.options.every((o, i) => o === chosen[i])) || null;

    const mediaHtml = (images) =>
      '<div class="cpqv__track">' +
      images
        .map(
          (src, n) =>
            '<div class="cpqv__slide"><img src="' + escapeHtml(src) + '" alt="' + (n === 0 ? escapeHtml(p.title) : '') + '" loading="' + (n === 0 ? 'eager' : 'lazy') + '" decoding="async"></div>'
        )
        .join('') +
      '</div>' +
      (images.length > 1
        ? '<div class="cpqv__dots" aria-hidden="true">' + images.map((_, n) => '<span' + (n === 0 ? ' class="is-active"' : '') + '></span>').join('') + '</div>' +
          '<button type="button" class="cpqv__nav cpqv__nav--prev" aria-label="Previous photo">‹</button>' +
          '<button type="button" class="cpqv__nav cpqv__nav--next" aria-label="Next photo">›</button>'
        : '');

    const optionsHtml = () =>
      p.variants.length < 2
        ? ''
        : p.options
            .map((opt, i) => {
              const values = valuesFor(p, i);
              const pool = matching(p, chosen, i);
              return (
                '<div class="cpss__row" role="group" aria-label="' + escapeHtml(optionLabel(opt.name)) + '">' +
                '<p class="cpss__label">' + escapeHtml(optionLabel(opt.name)) + ': <strong>' + escapeHtml(chosen[i] || '') + '</strong></p>' +
                '<div class="cpss__chips">' +
                values
                  .map((value) => {
                    const available = pool.some((v) => v.options[i] === value && v.available);
                    const selected = chosen[i] === value;
                    return (
                      '<button type="button" class="cpss__chip cpss__chip--pick' + (selected ? ' is-selected' : '') + (available ? '' : ' is-unavailable') + '" data-row="' + i + '" data-value="' + escapeHtml(value) + '" aria-pressed="' + (selected ? 'true' : 'false') + '">' +
                      '<span class="cpss__chip-value">' + escapeHtml(value) + '</span></button>'
                    );
                  })
                  .join('') +
                '</div></div>'
              );
            })
            .join('');

    const priceHtml = () => {
      const v = selectedVariant();
      if (!v) return '<span class="cpss__now">Unavailable</span>';
      return '<span class="cpss__now">' + escapeHtml(v.price) + '</span>' + (v.compare ? ' <s class="cpss__was">' + escapeHtml(v.compare) + '</s>' : '');
    };

    const d = openShell(
      '<div class="cpqv">' +
        '<div class="cpqv__media">' + mediaHtml(p.images.length ? p.images : p.image ? [p.image] : []) + '</div>' +
        '<div class="cpqv__info">' +
        '<a class="cpqv__title" id="cpss-title" href="' + escapeHtml(p.url) + '">' + escapeHtml(p.title) + '</a>' +
        '<p class="cpss__price cpqv__price">' + priceHtml() + '</p>' +
        '<div class="cpqv__options">' + optionsHtml() + '</div>' +
        '<div class="cpqv__buy">' +
        '<div class="cpqv__qty" role="group" aria-label="Quantity">' +
        '<button type="button" data-step="-1" aria-label="Decrease quantity">−</button>' +
        '<span class="cpqv__qty-num" aria-live="polite">1</span>' +
        '<button type="button" data-step="1" aria-label="Increase quantity">+</button>' +
        '</div>' +
        '<button type="button" class="cpqv__add">Add to cart</button>' +
        '</div>' +
        '<button type="button" class="cpqv__now">Buy it now</button>' +
        '<p class="cpss__error" role="alert" hidden></p>' +
        '<a class="cpqv__more" href="' + escapeHtml(p.url) + '">View full details</a>' +
        '</div></div>',
      'qv'
    );

    const info = d.querySelector('.cpqv__info');
    const priceEl = d.querySelector('.cpqv__price');
    const optionsEl = d.querySelector('.cpqv__options');
    const addBtn = d.querySelector('.cpqv__add');
    const nowBtn = d.querySelector('.cpqv__now');
    const error = d.querySelector('.cpss__error');
    const media = d.querySelector('.cpqv__media');

    const refresh = () => {
      const v = selectedVariant();
      priceEl.innerHTML = priceHtml();
      optionsEl.innerHTML = optionsHtml();
      const can = !!(v && v.available);
      addBtn.disabled = !can;
      nowBtn.disabled = !can;
      addBtn.textContent = v ? (v.available ? 'Add to cart' : 'Sold out') : 'Unavailable';
      if (v && v.image) showImage(v.image);
    };

    const wireMedia = () => {
      const track = media.querySelector('.cpqv__track');
      const dots = media.querySelectorAll('.cpqv__dots span');
      if (!track) return;
      track.addEventListener(
        'scroll',
        () => {
          const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          dots.forEach((dot, n) => dot.classList.toggle('is-active', n === i));
        },
        { passive: true }
      );
      media.querySelectorAll('.cpqv__nav').forEach((btn) =>
        btn.addEventListener('click', () => {
          const dir = btn.classList.contains('cpqv__nav--next') ? 1 : -1;
          track.scrollBy({ left: dir * track.clientWidth, behavior: 'smooth' });
        })
      );
    };

    const showImage = (src) => {
      const trackEl = media.querySelector('.cpqv__track');
      if (!trackEl) return;
      const imgs = [...trackEl.querySelectorAll('img')];
      const base = (u) => String(u).split('?')[0];
      const n = imgs.findIndex((img) => base(img.src) === base(src));
      if (n >= 0) trackEl.scrollTo({ left: n * trackEl.clientWidth, behavior: 'smooth' });
    };

    wireMedia();
    refresh();

    info.addEventListener('click', async (event) => {
      const t = event.target instanceof Element ? event.target : null;
      if (!t) return;

      const chip = t.closest('.cpss__chip--pick');
      if (chip) {
        const row = Number(chip.dataset.row);
        chosen[row] = chip.dataset.value;
        // Keep later choices valid.
        for (let i = row + 1; i < p.options.length; i++) {
          const pool = matching(p, chosen, i);
          if (!pool.some((v) => v.options[i] === chosen[i] && v.available)) {
            const pick = pool.find((v) => v.available) || pool[0];
            chosen[i] = pick ? pick.options[i] : null;
          }
        }
        refresh();
        return;
      }

      const step = t.closest('[data-step]');
      if (step) {
        qty = Math.max(1, Math.min(99, qty + Number(step.dataset.step)));
        d.querySelector('.cpqv__qty-num').textContent = String(qty);
        return;
      }

      const buyNow = t.closest('.cpqv__now');
      const add = t.closest('.cpqv__add');
      if (!add && !buyNow) return;
      const v = selectedVariant();
      if (!v || !v.available) return;

      const button = add || buyNow;
      button.classList.add('is-busy');
      addBtn.disabled = true;
      nowBtn.disabled = true;
      error.hidden = true;
      try {
        const json = await addToCart(v.id, qty);
        if (buyNow) {
          track('quickview_buynow');
          location.href = ROOT + 'checkout';
          return;
        }
        track('quickview_add');
        button.classList.remove('is-busy');
        button.classList.add('is-done');
        button.textContent = 'Added';
        setTimeout(() => {
          closeSheet();
          announceAdd(json);
        }, 420);
      } catch (e) {
        button.classList.remove('is-busy');
        error.textContent = e.message;
        error.hidden = false;
        refresh();
      }
    });

    track('quickview_open');

    // Bring in the rest of the photos (and variant images) in the background.
    if (!p.full) {
      loadFull(p.handle)
        .then((full) => {
          if (!dialog || !dialog.open || !dialog.classList.contains('cpss--qv')) return;
          if (!d.contains(media)) return;
          p.images = full.images;
          p.variants.forEach((v) => {
            const match = full.variants.find((f) => String(f.id) === String(v.id));
            if (match) v.image = match.image;
          });
          if (full.images.length > 1) {
            media.innerHTML = mediaHtml(full.images);
            wireMedia();
          }
        })
        .catch(() => {
          /* the first photo is already showing */
        });
    }
  };

  /* ------------------------------------------------------------------ */
  /* Eye buttons on cards                                               */
  /* ------------------------------------------------------------------ */

  const CARD_SELECTOR = 'product-card, .cprail-card';
  const SKIP_INSIDE = '.quick-add-modal, .cart-drawer, .search-modal, predictive-search-component, .cart-page';

  const addEyeButtons = () => {
    document.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      if (card.hasAttribute('data-cpqv')) return;
      if (card.closest(SKIP_INSIDE)) return;
      if (!card.querySelector('[data-cpss]')) return;
      const host = card.matches('.cprail-card') ? card.querySelector('.cprail-card__media') || card : card.querySelector('.card-gallery');
      if (!host) return;
      card.setAttribute('data-cpqv', '');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cpqv-btn';
      btn.setAttribute('aria-label', 'Quick view');
      btn.innerHTML = EYE;
      host.appendChild(btn);
    });
  };

  /* ------------------------------------------------------------------ */
  /* Triggers                                                           */
  /* ------------------------------------------------------------------ */

  const goTo = (url) => {
    if (url) location.href = url;
  };

  const TRIGGERS = [
    '.cpqv-btn',
    '.cpcard-cta[data-cpss-size]',
    '.cpc-up__btn--ghost',
    'sticky-add-to-cart .sticky-add-to-cart__button',
    'sticky-add-to-cart .sticky-add-to-cart__info',
    'sticky-add-to-cart .sticky-add-to-cart__image',
  ].join(', ');

  // Window capture phase: runs before the whole-card click handler
  // (cp-ux-fixes.js), the card's own link and the sticky bar's own handler.
  window.addEventListener(
    'click',
    (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const trigger = event.target instanceof Element ? event.target.closest(TRIGGERS) : null;
      if (!trigger) return;

      // Product page sticky bar
      const sticky = trigger.closest('sticky-add-to-cart');
      if (sticky) {
        const p = pdpData();
        // Single-size products keep the theme's own add (with its fly-to-cart).
        if (!isSized(p)) return;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        openSizeSheet(p, { currentId: sticky.dataset.currentVariantId });
        return;
      }

      // Eye button: Quick View
      if (trigger.matches('.cpqv-btn')) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        const card = trigger.closest(CARD_SELECTOR);
        const p = card && quickData(card);
        if (!p) {
          const link = card && card.querySelector('a[href*="/products/"]');
          goTo(link && link.href);
          return;
        }
        openQuickView(p);
        return;
      }

      // Card "Choose size"
      if (trigger.matches('.cpcard-cta[data-cpss-size]')) {
        const p = quickData(trigger);
        if (!isSized(p)) return; // let the link go to the product page
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        openSizeSheet(p);
        return;
      }

      // Cart drawer "You may also like" → "Choose size"
      if (trigger.matches('.cpc-up__btn--ghost')) {
        const handle = handleFromUrl(trigger.getAttribute('href'));
        if (!handle) return;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        trigger.classList.add('is-loading');
        loadFull(handle)
          .then((p) => (isSized(p) ? openSizeSheet(p) : openQuickView(p)))
          .catch(() => goTo(trigger.getAttribute('href')))
          .finally(() => trigger.classList.remove('is-loading'));
      }
    },
    true
  );

  // Warm the cache for the cart rail before the tap lands.
  const warm = (event) => {
    const t = event.target instanceof Element ? event.target.closest('.cpc-up__btn--ghost') : null;
    if (t) loadFull(handleFromUrl(t.getAttribute('href'))).catch(() => {});
  };
  document.addEventListener('pointerover', warm, { passive: true });
  document.addEventListener('touchstart', warm, { passive: true });

  /* ------------------------------------------------------------------ */
  /* Boot                                                               */
  /* ------------------------------------------------------------------ */

  let timer = 0;
  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(addEyeButtons, 150);
  };

  const start = () => {
    addEyeButtons();
    pdpData();
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.cpShopSheet = { openSizeSheet, openQuickView, close: closeSheet, load: loadFull };
})();
